import React from "react";

import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { fashionPaths } from "@/modules";
import { CartItem } from "../store/cart-store";
import { useCart } from "../hooks/use-cart";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface CartItemCardDesktopProps {
  item: CartItem;
  className?: string;
}

export const CartItemCardDesktop: React.FC<CartItemCardDesktopProps> = ({
  item,
  className,
}) => {
  const { t } = useTranslation();
  const { updateQuantity, removeFromCart, formatPrice: formatPriceHook } = useCart();

  // Safe formatPrice with NaN handling
  const formatPrice = (price: number | undefined | null) => {
    if (typeof price !== "number" || isNaN(price)) {
      return formatPriceHook(0);
    }
    return formatPriceHook(price);
  };

  const handleQuantityIncrease = () => {
    updateQuantity(item._id, item.quantity + 1);
  };

  const handleQuantityDecrease = () => {
    if (item.quantity > 1) {
      updateQuantity(item._id, item.quantity - 1);
    } else {
      removeFromCart(item._id);
    }
  };

  const handleRemove = () => {
    removeFromCart(item._id);
  };

  // Ürün detayını _product (Mongo ID) ile aç
  const productDetailHref = fashionPaths.product(item._product || item.productId);

  // Item price with NaN handling
  const itemPrice = typeof item.price === "number" && !isNaN(item.price) ? item.price : 0;
  const itemTotal = itemPrice * item.quantity;

  // Yeni data'dan variant bilgileri
  const variant1 = item.metadata?.axis1Name && item.metadata?.a1Value 
    ? { name: item.metadata.axis1Name, value: item.metadata.a1Value }
    : null;
  const variant2 = item.metadata?.axis2Name && item.metadata?.a2Value
    ? { name: item.metadata.axis2Name, value: item.metadata.a2Value }
    : null;

  // Campaign price bilgisi
  const originalPrice = item.metadata?.originalPrice;
  const hasDiscount = originalPrice && typeof originalPrice === 'number' && originalPrice > itemPrice;
  const discountPct = hasDiscount && typeof originalPrice === 'number'
    ? Math.round(((originalPrice - itemPrice) / originalPrice) * 100)
    : 0;

  // Stock bilgisi
  const stockCount = item.metadata?.stockCount as number | undefined;
  const inStock = item.metadata?.inStock !== false;

  return (
    <div
      className={cn(
        "flex gap-4 p-4 bg-card rounded-xl border border-border/50",
        "hover:border-border hover:shadow-md transition-all duration-300",
        className
      )}
    >
      {/* Product Image - Full Height */}
      <Link
        to={productDetailHref}
        className="block w-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 self-stretch cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all duration-200"
      >
        {item.file ? (
          <img
            src={item.file}
            alt={item.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8" />
          </div>
        )}
      </Link>

      {/* Content Area */}
      <div className="flex-1 flex flex-col justify-between min-h-[100px]">
        {/* Top Row: Product Details & Price */}
        <div className="flex justify-between items-start">
          {/* Left: Product Details */}
          <div className="flex-1 pr-4">
            <Link
              to={productDetailHref}
              className="block font-semibold text-lg leading-tight cursor-pointer hover:text-primary transition-colors duration-200"
            >
              {item.title}
            </Link>
            
            {/* Seller Name */}
            {item.metadata?.sellerName && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {item.metadata.sellerName}
              </p>
            )}

            {/* Variant Badges + Stock Warning - Same Line */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {variant1 && (
                <span className="px-2 py-0.5 rounded-md text-xs bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                  {variant1.value}
                </span>
              )}
              {variant2 && (
                <span className="px-2 py-0.5 rounded-md text-xs bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                  {variant2.value}
                </span>
              )}
              
              {/* Stock Warning */}
              {inStock && stockCount && stockCount < 10 && (
                <span className="px-2 py-0.5 rounded-md text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800 font-medium">
                  Son {stockCount} ürün!
                </span>
              )}
              {!inStock && (
                <span className="px-2 py-0.5 rounded-md text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 font-medium">
                  Stokta Yok
                </span>
              )}
            </div>
          </div>

          {/* Right: Price & Discount - Compact Layout */}
          <div className="flex flex-col items-end gap-1">
            {/* Price Section */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {hasDiscount && originalPrice ? (
                <>
                  <span className="text-sm line-through text-muted-foreground">
                    {formatPrice((originalPrice as number) * item.quantity)}
                  </span>
                  <span className="text-lg font-bold text-primary">
                    {formatPrice(itemTotal)}
                  </span>
                  <span className="px-1.5 py-0.5 bg-red-500 text-white rounded text-xs font-semibold">
                    %{discountPct}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-primary">
                  {formatPrice(itemTotal)}
                </span>
              )}
            </div>
            
            {/* Unit Price (if quantity > 1) */}
            {item.quantity > 1 && (
              <p className="text-sm text-muted-foreground">
                {formatPrice(itemPrice)} × {item.quantity}
              </p>
            )}
          </div>
        </div>

        {/* Bottom Row: Quantity Controls & Remove Button */}
        <div className="flex justify-between items-center mt-4">
          <div className="flex-1"></div> {/* Spacer */}
          <div className="flex items-center gap-4">
            {/* Quantity Controls */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleQuantityDecrease}
                  className={cn(
                    "h-8 w-8 p-0 group transition-all duration-200",
                    "hover:bg-gradient-pink-purple hover:text-white hover:border-pink-500/20"
                  )}
                >
                  <Minus className="h-4 w-4 group-hover:animate-pulse" />
                </Button>

                <span className="w-12 text-center font-medium text-lg">
                  {item.quantity}
                </span>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleQuantityIncrease}
                  className={cn(
                    "h-8 w-8 p-0 group transition-all duration-200",
                    "hover:bg-gradient-cyan-blue hover:text-white hover:border-cyan-500/20"
                  )}
                >
                  <Plus className="h-4 w-4 group-hover:animate-pulse" />
                </Button>
              </div>
            </div>

            {/* Remove Button */}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRemove}
              className={cn(
                "group transition-all duration-200 text-sm h-8",
                "hover:bg-red-500 hover:text-white hover:border-red-500/20",
                "text-red-600 border-red-200"
              )}
            >
              <Trash2 className="h-4 w-4 mr-2 group-hover:animate-pulse" />
              {t("cart.remove")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
